import { Box } from '@mui/material';
import { useState, useEffect, useCallback, ReactElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FlexContainer } from '../../containers/ContentContainers/FlexContainer';

interface SlideshowProps {
  slides: ReactElement[];
  autoAdvanceInterval?: number | number[];
  isActive?: boolean;
  onSlideChange?: (slideIndex: number) => void;
}

export const Slideshow = ({
  slides,
  autoAdvanceInterval,
  isActive = true,
  onSlideChange,
}: SlideshowProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => {
      // Stop on the last slide instead of looping
      if (prev >= slides.length - 1) {
        return prev;
      }
      const next = prev + 1;
      onSlideChange?.(next);
      return next;
    });
  }, [slides.length, onSlideChange]);

  // Auto-advance slides only if autoAdvanceInterval is provided
  useEffect(() => {
    if (!isActive || autoAdvanceInterval === undefined) {
      if (!isActive) {
        setCurrentSlide(0);
      }
      return;
    }

    // Get the interval for the current slide
    const getIntervalForSlide = (slideIndex: number): number | undefined => {
      if (Array.isArray(autoAdvanceInterval)) {
        // If it's an array, use the interval for the current slide, or the last one if out of bounds
        return (
          autoAdvanceInterval[slideIndex] ??
          autoAdvanceInterval[autoAdvanceInterval.length - 1]
        );
      }
      // If it's a single number, use it for all slides
      return autoAdvanceInterval;
    };

    const currentInterval = getIntervalForSlide(currentSlide);

    // If no interval for this slide, don't set up auto-advance
    if (currentInterval === undefined) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentSlide(prev => {
        // Stop on the last slide instead of looping
        if (prev >= slides.length - 1) {
          return prev;
        }
        const next = prev + 1;
        onSlideChange?.(next);
        return next;
      });
    }, currentInterval);

    return () => clearInterval(interval);
  }, [
    isActive,
    slides.length,
    onSlideChange,
    autoAdvanceInterval,
    currentSlide,
  ]);

  // Reset to first slide when slides change
  useEffect(() => {
    setCurrentSlide(0);
  }, [slides.length]);

  return (
    <Box
      onClick={nextSlide}
      sx={{
        width: '100%',
        height: '100%',
        cursor: 'pointer',
        background:
          'linear-gradient(75deg, #81CDC3 0%, #021E38 25%, #021E38 50%, #FF7B65 85%, #FFEDDA 100%)',
      }}
    >
      <FlexContainer
        direction="column"
        width="100%"
        height="100%"
        padding="24px"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            style={{ width: '100%', height: '100%' }}
          >
            {slides[currentSlide]}
          </motion.div>
        </AnimatePresence>
      </FlexContainer>
    </Box>
  );
};
